import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { PlanId, Subscription, Plan } from '../types';
import { PLANS } from '../types';
import { useAuth, useAuthenticatedFetch } from './AuthContext';

interface PlanContextType {
  subscription: Subscription;
  currentPlan: Plan;
  canCreatePost: () => boolean;
  incrementPostUsage: () => void;
  resetMonthlyUsage: () => void;
  postsRemaining: () => number;
  isFeatureAvailable: (feature: 'branding' | 'prioritySupport' | 'multipleClients') => boolean;
  showWatermark: () => boolean;
  canUploadFiles: () => boolean;
  // Stripe integration
  checkout: (planId: PlanId) => Promise<{ success: boolean; error?: string }>;
  manageBilling: () => Promise<{ success: boolean; error?: string }>;
  isCheckoutLoading: boolean;
}

const PlanContext = createContext<PlanContextType | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const authFetch = useAuthenticatedFetch();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  // Build subscription from user data (synced with database)
  const [subscription, setSubscription] = useState<Subscription>(() => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      planId: user?.planId || 'free',
      status: 'active',
      currentPeriodStart: new Date(now.getFullYear(), now.getMonth(), 1),
      currentPeriodEnd: endOfMonth,
      postsUsedThisMonth: user?.postsThisMonth || 0,
      storageUsedBytes: 0,
    };
  });

  // Sync subscription with user data from auth (plan + post count)
  useEffect(() => {
    if (user) {
      setSubscription(prev => {
        const updated = { ...prev };
        if (user.planId && user.planId !== prev.planId) {
          updated.planId = user.planId;
        }
        // Sync posts count from server (this is the source of truth)
        if (user.postsThisMonth !== undefined) {
          updated.postsUsedThisMonth = user.postsThisMonth;
        }
        return updated;
      });
    }
  }, [user?.planId, user?.postsThisMonth]);

  // Check if we need to reset monthly usage
  useEffect(() => {
    const now = new Date();
    if (now > subscription.currentPeriodEnd) {
      const newStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const newEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setSubscription(prev => ({
        ...prev,
        currentPeriodStart: newStart,
        currentPeriodEnd: newEnd,
        postsUsedThisMonth: 0,
      }));
    }
  }, [subscription.currentPeriodEnd]);

  const currentPlan = PLANS[subscription.planId];

  const canCreatePost = useCallback(() => {
    return subscription.postsUsedThisMonth < currentPlan.features.postsPerMonth;
  }, [currentPlan, subscription.postsUsedThisMonth]);

  const incrementPostUsage = useCallback(() => {
    setSubscription(prev => ({
      ...prev,
      postsUsedThisMonth: prev.postsUsedThisMonth + 1,
    }));
  }, []);

  const resetMonthlyUsage = useCallback(() => {
    setSubscription(prev => ({
      ...prev,
      postsUsedThisMonth: 0,
    }));
  }, []);

  const postsRemaining = useCallback(() => {
    return Math.max(0, currentPlan.features.postsPerMonth - subscription.postsUsedThisMonth);
  }, [currentPlan, subscription.postsUsedThisMonth]);

  const isFeatureAvailable = useCallback((feature: 'branding' | 'prioritySupport' | 'multipleClients') => {
    switch (feature) {
      case 'branding':
        return currentPlan.features.branding !== 'watermark';
      case 'prioritySupport':
        return currentPlan.features.prioritySupport;
      case 'multipleClients':
        return currentPlan.features.clients > 1;
      default:
        return false;
    }
  }, [currentPlan]);

  const showWatermark = useCallback(() => {
    return currentPlan.features.branding === 'watermark';
  }, [currentPlan]);

  // File uploads are available for all paid plans (non-free)
  const canUploadFiles = useCallback(() => {
    return subscription.planId !== 'free';
  }, [subscription.planId]);

  // Stripe checkout - redirect to Stripe payment page
  const checkout = useCallback(async (planId: PlanId): Promise<{ success: boolean; error?: string }> => {
    if (planId === 'free') {
      return { success: false, error: 'Cannot checkout for free plan' };
    }

    setIsCheckoutLoading(true);
    try {
      const response = await authFetch('/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Checkout failed' };
      }

      // Redirect to Stripe checkout
      window.location.href = data.url;
      return { success: true };
    } catch (error) {
      console.error('Checkout error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsCheckoutLoading(false);
    }
  }, [authFetch]);

  // Stripe billing portal - manage subscription
  const manageBilling = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    setIsCheckoutLoading(true);
    try {
      const response = await authFetch('/stripe/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to open billing portal' };
      }

      // Redirect to Stripe billing portal
      window.location.href = data.url;
      return { success: true };
    } catch (error) {
      console.error('Billing portal error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsCheckoutLoading(false);
    }
  }, [authFetch]);

  return (
    <PlanContext.Provider
      value={{
        subscription,
        currentPlan,
        canCreatePost,
        incrementPostUsage,
        resetMonthlyUsage,
        postsRemaining,
        isFeatureAvailable,
        showWatermark,
        canUploadFiles,
        checkout,
        manageBilling,
        isCheckoutLoading,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}

// Default value for when context isn't available yet (during initial render)
const DEFAULT_PLAN_VALUE: PlanContextType = {
  subscription: {
    planId: 'free',
    status: 'active',
    currentPeriodStart: new Date(),
    currentPeriodEnd: new Date(),
    postsUsedThisMonth: 0,
    storageUsedBytes: 0,
  },
  currentPlan: PLANS.free,
  canCreatePost: () => true,
  incrementPostUsage: () => {},
  resetMonthlyUsage: () => {},
  postsRemaining: () => 30,
  isFeatureAvailable: () => false,
  showWatermark: () => true,
  canUploadFiles: () => false,
  checkout: async () => ({ success: false, error: 'Not initialized' }),
  manageBilling: async () => ({ success: false, error: 'Not initialized' }),
  isCheckoutLoading: false,
};

export function usePlan() {
  const context = useContext(PlanContext);
  // Return default value if context not ready (prevents crashes during initial render)
  return context || DEFAULT_PLAN_VALUE;
}
